import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Calendar, 
  Users,
  Star,
  Award,
  Target
} from 'lucide-react';
import Card from '../../components/UI/Card';
import ContactPreview from '../../components/Home/ContactPreview';

const EventManagement: React.FC = () => {
  const services = [
    {
      icon: Calendar,
      title: "Corporate Events",
      description: "Professional corporate event planning including conferences like the Global Maritime India Summit 2023 at BKC Mumbai and innovation challenges like Goa Open Innovation Challenge."
    },
    {
      icon: Users,
      title: "Government Functions",
      description: "Specialized management of government policy launches including PM Vishwakarma Yojana, Bhima Sakhi Yojana, and Jal Jeevan Mission events with full protocol management."
    },
    {
      icon: Star,
      title: "Cultural Festivals",
      description: "Complete cultural event management including 51st & 52nd International Film Festival of India with thematic art installations, opening/closing ceremonies, and entertainment zones."
    },
    {
      icon: Target,
      title: "Trade Shows & Exhibitions",
      description: "End-to-end exhibition management including India International Trade Fair (IITF) 2024 at Bharat Mandapam, Goa Krishi Mohatsav, and Aqua Goa Mega Fish Festival series."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Event Management - Mirchi Republic Media Production</title>
        <meta name="description" content="Professional event management services for corporate events, government functions, cultural festivals, and trade shows. Expert planning and flawless execution." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-brand-1 to-brand-2 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 800 600" className="w-full h-full" fill="currentColor">
              <defs>
                <pattern id="event-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                  <circle cx="30" cy="30" r="3" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="800" height="600" fill="url(#event-pattern)" />
            </svg>
          </div>
          
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1 
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Event Management
              </motion.h1>
              <motion.p 
                className="text-xl lg:text-2xl opacity-90 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Creating Memorable Experiences Through Expert Event Planning and Flawless Execution
              </motion.p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Professional Event Management</h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  We specialize in managing large-scale events including government functions, cultural festivals, 
                  corporate conferences, and international summits. Our experience includes prestigious events like 
                  the Global Maritime India Summit, International Film Festival of India, and major government policy launches.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">Our Event Management Services</h2>
                <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
                  Comprehensive event solutions tailored to your specific needs and objectives
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-6 h-full text-center hover:shadow-lg transition-shadow duration-300">
                      <service.icon className="w-16 h-16 text-brand-1 mx-auto mb-4" />
                      <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                      <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Why Choose Mirchi Republic?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <Card className="p-6 text-center">
                    <Award className="w-12 h-12 text-brand-1 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Proven Experience</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      15+ years managing high-profile events for government and corporate clients
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <Users className="w-12 h-12 text-brand-1 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Expert Team</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Dedicated professionals with specialized expertise in various event domains
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <Target className="w-12 h-12 text-brand-1 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Result-Oriented</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Focus on achieving your event objectives and exceeding expectations
                    </p>
                  </Card>
                </div>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Let us transform your vision into reality with our comprehensive event management expertise. 
                  From conceptualization to execution, we ensure every detail is perfect.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactPreview />
      </div>
    </>
  );
};

export default EventManagement;